import { AnchorNavigationEvents } from '@frontend/utils/AnchorNavigationUtil';

export default class BaseTabs {
	public static events = {
		TABS_CHANGED: 'BaseTabs.changed'
	};

	private static domSelectors = {
		pane: '[data-tabs-pane]',
		trigger: '[data-tabs-trigger]'
	};

	private static stateClasses = {
		hasInitializedTabs: 'has-initialized-tabs',
		isDisplayed: 'is-displayed',
		isActive: 'is-active'
	};

	private currentTab?: string | null;

	private readonly element: HTMLElement;

	private readonly options: any;

	public constructor(element: HTMLElement, options?: IBaseTabOptions) {
		this.element = element;
		this.options = options;
		this.init();
	}

	public init(): void {
		if (this.options && this.options.initialTab) {
			this.currentTab = this.options.initialTab;
		} else {
			const triggers: HTMLElement[] = Array.from(this.element.querySelectorAll(BaseTabs.domSelectors.trigger));
			const firstTrigger = triggers[0];
			const id = BaseTabs.getIdOfElement(firstTrigger);
			if (id) {
				this.currentTab = id;
			}
		}
		this.initEvents();
		this.updateUI();
		this.element.classList.add(BaseTabs.stateClasses.hasInitializedTabs);

		setTimeout((): void => {
			this.emitTabChange();
		}, 20);
	}

	public destroy(): void {
		this.removeEvents();
		this.resetUI();
		this.currentTab = null;
	}

	public getElement(): HTMLElement {
		return this.element;
	}

	private initEvents(): void {
		this.triggerClickHandler = this.triggerClickHandler.bind(this);
		this.anchorLinkHandler = this.anchorLinkHandler.bind(this);

		const triggers: HTMLElement[] = Array.from(this.element.querySelectorAll(BaseTabs.domSelectors.trigger));

		triggers.forEach((trigger): void => {
			trigger.addEventListener('click', this.triggerClickHandler);
		});

		document.addEventListener(AnchorNavigationEvents.change, this.anchorLinkHandler);
	}

	private removeEvents(): void {
		const triggers: HTMLElement[] = Array.from(this.element.querySelectorAll(BaseTabs.domSelectors.trigger));

		triggers.forEach((trigger): void => {
			trigger.removeEventListener('click', this.triggerClickHandler);
		});

		document.removeEventListener(AnchorNavigationEvents.change, this.anchorLinkHandler);
	}

	private triggerClickHandler(event: MouseEvent): void {
		event.preventDefault();
		const element: EventTarget | null = event.target;
		const id = BaseTabs.getIdOfElement(element as HTMLElement);

		if (id) {
			this.currentTab = id;
			this.updateUI();
			this.emitTabChange();
		}
	}

	private anchorLinkHandler(event: CustomEventInit): void {
		const target = event.detail.target;
		const id = target.replace('#', '');
		const targetElement = this.element.querySelector(target);

		if (targetElement) {
			this.currentTab = id;
			this.updateUI();
			this.emitTabChange();
		}
	}

	private updateUI(): void {
		const panes: HTMLElement[] = Array.from(this.element.querySelectorAll(BaseTabs.domSelectors.pane));
		const triggers: HTMLElement[] = Array.from(this.element.querySelectorAll(BaseTabs.domSelectors.trigger));

		panes.forEach((pane): void => {
			const paneId = pane.getAttribute('data-tabs-pane');
			if (this.currentTab === paneId) {
				pane.classList.add(BaseTabs.stateClasses.isDisplayed);
				setTimeout((): void => {
					pane.classList.add(BaseTabs.stateClasses.isActive);
				}, 80);
			} else {
				pane.classList.remove(BaseTabs.stateClasses.isDisplayed, BaseTabs.stateClasses.isActive);
			}
		});

		triggers.forEach((trigger): void => {
			const triggerId = BaseTabs.getIdOfElement(trigger);
			if (this.currentTab === triggerId) {
				trigger.classList.add(BaseTabs.stateClasses.isActive);
			} else {
				trigger.classList.remove(BaseTabs.stateClasses.isActive);
			}
		});
	}

	private resetUI(): void {
		const panes: HTMLElement[] = Array.from(this.element.querySelectorAll(BaseTabs.domSelectors.pane));
		const triggers: HTMLElement[] = Array.from(this.element.querySelectorAll(BaseTabs.domSelectors.trigger));

		[...panes, ...triggers].forEach((element): void => {
			element.classList.remove(BaseTabs.stateClasses.isDisplayed, BaseTabs.stateClasses.isActive);
		});

		this.element.classList.remove(BaseTabs.stateClasses.hasInitializedTabs);
	}

	private static getIdOfElement(element: HTMLElement): string | null {
		const id = element.getAttribute('href');
		if (!id) return null;
		return id.replace('#', '');
	}

	private getActiveTabElement(): HTMLElement | null {
		return this.element.querySelector(`.${BaseTabs.stateClasses.isDisplayed}`);
	}

	private emitTabChange(): void {
		this.element.dispatchEvent(
			new CustomEvent(BaseTabs.events.TABS_CHANGED, {
				detail: {
					currentTab: this.currentTab,
					selectedElement: this.getActiveTabElement()
				}
			})
		);
	}
}

export interface IBaseTabOptions {
	initialTab?: string;
}
